"use client"

import { useMemo, useState } from "react"

type SubmissionValues = {
  title: string
  description: string
  ingredients: string
  steps: string
  authorName: string
  authorEmail: string
  notes: string
}

type SubmissionErrors = Partial<Record<keyof SubmissionValues, string>>

type SubmissionApiErrorResponse = {
  ok: false
  message: string
  errors?: SubmissionErrors
}

const initialValues: SubmissionValues = {
  title: "",
  description: "",
  ingredients: "",
  steps: "",
  authorName: "",
  authorEmail: "",
  notes: "",
}

function isValidEmail(email: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
}

export function SubmissionForm() {
  const [values, setValues] = useState<SubmissionValues>(initialValues)
  const [errors, setErrors] = useState<SubmissionErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [serverMessage, setServerMessage] = useState("")

  const isFormValid = useMemo(() => {
    return (
      values.title.trim().length >= 4 &&
      values.description.trim().length >= 20 &&
      values.ingredients.trim().length >= 10 &&
      values.steps.trim().length >= 20 &&
      values.authorName.trim().length >= 2 &&
      isValidEmail(values.authorEmail.trim())
    )
  }, [values])

  function setField<K extends keyof SubmissionValues>(
    field: K,
    value: SubmissionValues[K]
  ) {
    setValues((prev) => ({
      ...prev,
      [field]: value,
    }))

    setErrors((prev) => ({
      ...prev,
      [field]: undefined,
    }))

    setServerMessage("")
  }

  function validate() {
    const nextErrors: SubmissionErrors = {}

    if (values.title.trim().length < 4) {
      nextErrors.title = "Назва рецепта має містити щонайменше 4 символи."
    }

    if (values.description.trim().length < 20) {
      nextErrors.description =
        "Короткий опис має містити щонайменше 20 символів."
    }

    if (values.ingredients.trim().length < 10) {
      nextErrors.ingredients = "Додайте список інгредієнтів."
    }

    if (values.steps.trim().length < 20) {
      nextErrors.steps = "Опишіть кроки приготування більш детально."
    }

    if (values.authorName.trim().length < 2) {
      nextErrors.authorName = "Вкажіть ім’я довжиною щонайменше 2 символи."
    }

    if (!isValidEmail(values.authorEmail.trim())) {
      nextErrors.authorEmail = "Вкажіть коректну email-адресу."
    }

    setErrors(nextErrors)

    return Object.keys(nextErrors).length === 0
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()

    if (!validate()) {
      return
    }

    setIsSubmitting(true)
    setServerMessage("")

    try {
      const response = await fetch("/api/submissions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      })

      const result = (await response.json()) as
        | { ok: true; message: string }
        | SubmissionApiErrorResponse

      if (!response.ok || !result.ok) {
        if ("errors" in result && result.errors) {
          setErrors(result.errors)
        }

        setServerMessage(result.message || "Не вдалося надіслати рецепт.")
        return
      }

      setValues(initialValues)
      setErrors({})
      setIsSubmitted(true)
      setServerMessage(result.message)

      window.setTimeout(() => {
        setIsSubmitted(false)
      }, 3500)
    } catch {
      setServerMessage("Сталася помилка мережі. Спробуйте ще раз.")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8"
      noValidate
    >
      <div className="mb-6">
        <div className="eyebrow">Надіслати рецепт</div>

        <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
          Поділіться своїм рецептом
        </h2>

        <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
          Чим точніше й зрозуміліше оформлений рецепт, тим легше його перевірити
          та підготувати до публікації. Найбільше допомагають чіткі інгредієнти,
          послідовні кроки й короткі корисні примітки.
        </p>
      </div>

      <div className="grid gap-4">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Назва рецепта
          </span>
          <input
            type="text"
            value={values.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="Наприклад, мариновані огірки без стерилізації"
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.title ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.title}
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Короткий опис
          </span>
          <textarea
            rows={3}
            value={values.description}
            onChange={(e) => setField("description", e.target.value)}
            placeholder="Коротко опишіть, чим хороший цей рецепт і для чого він підходить"
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-7 text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.description ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.description}
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Інгредієнти
          </span>
          <textarea
            rows={5}
            value={values.ingredients}
            onChange={(e) => setField("ingredients", e.target.value)}
            placeholder={
              "Наприклад:\nОгірки — 2 кг\nСіль — 2 ст. л.\nЦукор — 3 ст. л."
            }
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-7 text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.ingredients ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.ingredients}
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Кроки приготування
          </span>
          <textarea
            rows={7}
            value={values.steps}
            onChange={(e) => setField("steps", e.target.value)}
            placeholder={
              "Опишіть рецепт покроково.\n1. Підготувати овочі...\n2. Простерилізувати банки...\n3. Приготувати маринад..."
            }
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-7 text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.steps ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.steps}
            </span>
          ) : null}
        </label>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Ваше ім’я
          </span>
          <input
            type="text"
            value={values.authorName}
            onChange={(e) => setField("authorName", e.target.value)}
            placeholder="Ваше ім’я"
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.authorName ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.authorName}
            </span>
          ) : null}
        </label>

        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Email
          </span>
          <input
            type="email"
            value={values.authorEmail}
            onChange={(e) => setField("authorEmail", e.target.value)}
            placeholder="you@example.com"
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm text-neutral-900 outline-none transition focus:border-emerald-400"
          />
          {errors.authorEmail ? (
            <span className="mt-2 block text-xs text-rose-600">
              {errors.authorEmail}
            </span>
          ) : null}
        </label>
      </div>

      <div className="mt-4">
        <label className="block">
          <span className="mb-2 block text-sm font-medium text-neutral-800">
            Додаткові нотатки
          </span>
          <textarea
            rows={3}
            value={values.notes}
            onChange={(e) => setField("notes", e.target.value)}
            placeholder="Наприклад, особливості зберігання, заміни інгредієнтів або ваші поради"
            className="w-full rounded-2xl border border-neutral-200 bg-white px-4 py-3 text-sm leading-7 text-neutral-900 outline-none transition focus:border-emerald-400"
          />
        </label>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <button
          type="submit"
          disabled={isSubmitting || !isFormValid}
          className="button-primary disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isSubmitting ? "Надсилаємо..." : "Надіслати рецепт"}
        </button>

        {isSubmitted ? (
          <div className="text-sm font-medium text-emerald-700">
            Рецепт успішно надіслано
          </div>
        ) : null}
      </div>

      {serverMessage ? (
        <div className="mt-4 text-sm text-neutral-600">{serverMessage}</div>
      ) : null}
    </form>
  )
}